import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { getJobPlan, isFreeJobPlan } from '../../config/jobPostingPlans';
import { CARD_BRANDS, CardBrandIcon } from '../../components/CardBrandIcons';

const CRYPTO_OPTIONS = [
  { id: 'eth', name: 'ETH', symbol: 'ERC20', icon: '⟠' },
  { id: 'bnb', name: 'BNB', symbol: 'BEP20', icon: '🟡' },
  { id: 'usdt', name: 'USDT', symbol: 'ERC20', icon: '🔷' },
];

function JobPaymentStep({ jobPosting, onBack, onPay, loading, error }) {
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [selectedCardBrand, setSelectedCardBrand] = useState('visa');
  const [selectedCrypto, setSelectedCrypto] = useState('eth');
  const [cardPreview, setCardPreview] = useState({
    cardholderName: '',
    cardNumber: '',
    expiry: '',
    cvc: '',
    country: '',
    postalCode: '',
  });
  const plan = getJobPlan(jobPosting?.plan);
  const isFree = isFreeJobPlan(jobPosting?.plan) || plan.price <= 0;

  return (
    <Box className="post-job-card ch-card-enter">
      <Box className="post-job-card-header" sx={{ mb: 1.5 }}>
        <Typography className="post-job-eyebrow" variant="overline" color="primary">
          Step 3 of 3
        </Typography>
        <Typography variant="h5" component="h2" sx={{ mt: 0.25, mb: 0.5 }}>
          {isFree ? 'Publish' : 'Payment'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isFree
            ? 'Your Basic plan is free. Confirm to publish your job posting.'
            : 'Pay securely by card or cryptocurrency to publish your job posting.'}
        </Typography>
      </Box>

      <Box className="post-job-summary" sx={{ mb: 1.5 }}>
        <div>
          <span>Job title</span>
          <strong>{jobPosting?.title}</strong>
        </div>
        <div>
          <span>Plan</span>
          <strong>{plan.name}</strong>
        </div>
        <div>
          <span>Total</span>
          <strong>{isFree ? 'Free' : `$${plan.price.toFixed(2)}`}</strong>
        </div>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.25 }}>
          {error}
        </Alert>
      )}

      {!isFree && (
        <>
          <Tabs
            value={paymentMethod}
            onChange={(_, value) => setPaymentMethod(value)}
            sx={{ mb: 1.25, minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 } }}
          >
            <Tab value="card" label="Card" />
            <Tab value="crypto" label="Crypto" />
          </Tabs>

          {paymentMethod === 'card' && (
            <Box sx={{ mb: 1.5 }}>
              <Box className="post-job-card-brands" role="group" aria-label="Card brand">
                {CARD_BRANDS.map((brand) => (
                  <button
                    key={brand.id}
                    type="button"
                    className={`brand-chip${selectedCardBrand === brand.id ? ' selected' : ''}`}
                    onClick={() => setSelectedCardBrand(brand.id)}
                    aria-pressed={selectedCardBrand === brand.id}
                  >
                    <CardBrandIcon brand={brand.id} />
                    <span className="brand-chip-label">{brand.name}</span>
                  </button>
                ))}
              </Box>

              <Stack spacing={1.25} sx={{ mt: 1.25 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  <TextField
                    label="Cardholder name"
                    placeholder="Jane Smith"
                    value={cardPreview.cardholderName}
                    onChange={(e) => setCardPreview((prev) => ({ ...prev, cardholderName: e.target.value }))}
                  />
                  <TextField
                    label="Card number"
                    placeholder="1234 5678 9012 3456"
                    value={cardPreview.cardNumber}
                    onChange={(e) => setCardPreview((prev) => ({ ...prev, cardNumber: e.target.value }))}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  <TextField
                    label="Expiry date"
                    placeholder="MM/YY"
                    value={cardPreview.expiry}
                    onChange={(e) => setCardPreview((prev) => ({ ...prev, expiry: e.target.value }))}
                  />
                  <TextField
                    label="CVC"
                    placeholder="123"
                    value={cardPreview.cvc}
                    onChange={(e) => setCardPreview((prev) => ({ ...prev, cvc: e.target.value }))}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  <TextField
                    label="Country"
                    placeholder="United States"
                    value={cardPreview.country}
                    onChange={(e) => setCardPreview((prev) => ({ ...prev, country: e.target.value }))}
                  />
                  <TextField
                    label="Postal code"
                    placeholder="10001"
                    value={cardPreview.postalCode}
                    onChange={(e) => setCardPreview((prev) => ({ ...prev, postalCode: e.target.value }))}
                  />
                </Stack>
              </Stack>

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.25 }}>
                You will be redirected to a secure hosted checkout page to complete payment. Card data is not stored by CofounderHubs.
              </Typography>
            </Box>
          )}

          {paymentMethod === 'crypto' && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
                Select cryptocurrency
              </Typography>
              <Box className="post-job-crypto-grid">
                {CRYPTO_OPTIONS.map((crypto) => (
                  <button
                    key={crypto.id}
                    type="button"
                    className={`post-job-crypto-option${selectedCrypto === crypto.id ? ' selected' : ''}`}
                    onClick={() => setSelectedCrypto(crypto.id)}
                  >
                    <span>{crypto.icon}</span>
                    <strong>{crypto.name}</strong>
                    <small>{crypto.symbol}</small>
                  </button>
                ))}
              </Box>
              <Typography variant="body2" color="text.secondary">
                You will receive payment instructions on the next screen.
              </Typography>
            </Box>
          )}
        </>
      )}

      {isFree && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          No payment is required for the Basic plan. Click below to publish your job listing.
        </Typography>
      )}

      <Box className="post-job-actions" sx={{ mt: 0.5 }}>
        <Button type="button" variant="outlined" onClick={onBack} disabled={loading}>
          Back
        </Button>
        <Button
          type="button"
          variant="contained"
          disabled={loading}
          onClick={() =>
            onPay({
              paymentMethod: isFree ? 'free' : paymentMethod,
              crypto: selectedCrypto,
            })
          }
        >
          {loading
            ? 'Processing...'
            : isFree
              ? 'Publish for Free'
              : `Pay $${plan.price.toFixed(2)}`}
        </Button>
      </Box>
    </Box>
  );
}

export default JobPaymentStep;
